import { onMounted, onUnmounted } from 'vue'

/**
 * Composable for handling click outside events
 * @param {Object} options - Configuration options
 * @param {Ref|Function} options.isOpen - Reactive reference or function returning open state
 * @param {Function} options.onClose - Callback when clicking outside
 * @param {Ref} options.containerRef - Reference to container element to check
 * @param {Ref} options.dropdownRef - Reference to dropdown element to check
 * @param {Array<string>} options.excludeSelectors - CSS selectors to exclude from click outside detection
 * @returns {Object} Click outside utilities
 */
export function useClickOutside(options = {}) {
  const {
    isOpen,
    onClose,
    containerRef = null,
    dropdownRef = null,
    excludeSelectors = []
  } = options

  const handleClickOutside = (event) => {
    const isCurrentlyOpen = typeof isOpen === 'function' ? isOpen() : isOpen?.value
    if (!isCurrentlyOpen) return

    // Check if click is on excluded elements
    for (const selector of excludeSelectors) {
      const elements = document.querySelectorAll(selector)
      for (const element of elements) {
        if (element.contains(event.target)) {
          return
        }
      }
    }

    // Check if click is on the container or dropdown itself
    if (containerRef?.value && containerRef.value.contains(event.target)) {
      return
    }
    if (dropdownRef?.value && dropdownRef.value.contains(event.target)) {
      return
    }

    // Call the close callback
    if (onClose && typeof onClose === 'function') {
      onClose()
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })

  return {
    handleClickOutside
  }
}

